import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqs } from '@/mocks/data';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector('input[name="phone_alt"]') as HTMLInputElement;
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9pn7odi2vhsdh2d37ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于爱情岛论坛亚洲',
    url: 'https://www.aiqingdao.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '爱情岛论坛亚洲',
      description: '亚洲最热情感交流社区，专为亚洲用户打造的情感交流平台',
      url: 'https://www.aiqingdao.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      {/* Page Header */}
      <section className="relative w-full h-[280px] md:h-[340px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20soft%20abstract%20pastel%20composition%20with%20cream%20peach%20and%20rose%20gentle%20gradients%2C%20minimal%20elegant%20style%2C%20dreamy%20ethereal%20atmosphere%2C%20flowing%20organic%20shapes&width=1600&height=600&seq=about-hero-2026&orientation=landscape"
          alt="关于爱情岛论坛亚洲"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-white/85 max-w-xl">
            了解爱情岛论坛亚洲的品牌故事与使命
          </p>
        </div>
      </section>

      <div className="py-8 md:py-10 px-4 md:px-6">
        <div className="w-full max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Introduction */}
          <section className="mb-14">
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    <strong>爱情岛论坛亚洲</strong>成立于2024年，是专为亚洲用户打造的一站式情感交流平台。
                    我们致力于为每一位在感情路上探索的人提供温暖、专业、有价值的交流空间。
                  </p>
                  <p>
                    无论你正在经历甜蜜的热恋、面临感情的困惑、准备步入婚姻的殿堂，还是希望提升自己的情感智慧，
                    爱情岛论坛亚洲都能为你提供丰富的资源和真诚的陪伴。
                  </p>
                  <p>
                    我们的内容涵盖<strong>恋爱技巧、婚恋话题、情感咨询、约会故事、心理成长、两性沟通</strong>等多个领域，
                    由专业心理咨询师、情感领域作家和经验丰富的编辑团队共同打造。
                  </p>
                  <p>
                    截至目前，平台已累计发布超过500篇高质量原创文章，吸引了来自亚洲各地的数万名活跃用户。
                    我们相信，每一段感情都值得被认真对待，每一个人都值得拥有幸福的爱情。
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-[300px] flex-shrink-0">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20cozy%20workspace%20with%20notebook%20and%20soft%20natural%20light%2C%20cream%20and%20peach%20pastel%20tones%2C%20peaceful%20atmosphere%2C%20creative%20writing%20space&width=600&height=600&seq=about-brand-2026&orientation=squarish"
                    alt="爱情岛论坛亚洲品牌"
                    title="爱情岛论坛亚洲 - 品牌形象"
                    className="w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="mb-14 py-8 px-6 rounded-2xl bg-background-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                  <i className="ri-heart-pulse-line text-xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  为亚洲用户提供专业、温暖的情感交流平台，帮助每个人在感情中找到方向与力量。
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className="ri-eye-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为亚洲最具影响力的情感交流社区，让每一颗心都能在这里找到归属与共鸣。
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-3">
                  <i className="ri-shield-star-line text-xl text-secondary-600"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">核心价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  真诚、尊重、包容、成长。我们相信每个人的感情故事都值得被认真倾听。
                </p>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-xl bg-background-100 flex flex-col gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.location}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 p-5 cursor-pointer">
                  <summary className="flex items-center justify-between text-sm font-medium text-foreground-900 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（限500字）"
                    ></textarea>
                  </div>
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="spam-field" />
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    提交留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 mt-2">感谢您的留言！我们会尽快与您联系。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 mt-2">{formError}</p>
                  )}
                </form>
              </div>
              <div className="lg:w-[300px] flex-shrink-0 space-y-4">
                <div className="p-5 rounded-xl bg-background-100">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-mail-line text-primary-500"></i>
                      contact@aiqingdao.com
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-phone-line text-primary-500"></i>
                      400-888-8888
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-primary-500"></i>
                      中国·亚洲
                    </p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">工作时间</h3>
                  <p className="text-sm text-foreground-600">周一至周五</p>
                  <p className="text-sm text-foreground-600">09:00 - 18:00</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}